<template>
 <div>
  <div class="container-fluid pt-3 pb-0">
    <div class="d-flex" style="gap:8px;">
      <button
        type="button"
        class="btn btn-sm"
        :class="activeTab === 'mpesa' ? 'text-white' : 'btn-light'"
        :style="activeTab === 'mpesa' ? { background: '#fd4719', borderColor: '#fd4719' } : {}"
        @click="setTab('mpesa')"
      >M-Pesa Sync</button>
      <button
        v-if="userDetails?.company?.companysettings?.has_invoices"
        type="button"
        class="btn btn-sm"
        :class="activeTab === 'ledger' ? 'text-white' : 'btn-light'"
        :style="activeTab === 'ledger' ? { background: '#fd4719', borderColor: '#fd4719' } : {}"
        @click="setTab('ledger')"
      >Ledger</button>
    </div>
  </div>

  <template v-if="activeTab === 'mpesa'">
  <div class="container-fluid pt-2" v-if="mpesaConfig">
    <div class="d-flex justify-content-center align-items-center" style="height:100vh">
      <div class="text-center">
        <h1><i class="bi bi-receipt"></i></h1>
        <p>To view M-Pesa transactions here, you need to configure M-Pesa keys.
          <a v-if="userDetails.roles?.[0]?.name === 'Admin'" href="/settings/payments">Go to settings</a>
        </p>
      </div>
    </div>
  </div>

  <div v-else class="container-fluid pt-3">

    <!-- Header -->
    <div class="row align-items-center mb-3">
      <div class="col-md-6">
        <h4 class="mb-0 font-weight-bold">Payment Transactions</h4>
      </div>
      <div class="col-md-6 text-right">
        <button class="btn btn-outline-secondary btn-sm mr-2" @click="load()">
          <i class="bi bi-arrow-clockwise"></i> Refresh
        </button>
        <button class="btn btn-outline-dark btn-sm" @click="exportCSV">
          <i class="bi bi-download"></i> Export CSV
        </button>
      </div>
    </div>

    <!-- Summary cards -->
    <div class="row mb-4">
      <div class="col-6 col-md mb-2" v-for="card in summaryCards" :key="card.label">
        <div class="card border h-100">
          <div class="card-body py-2 px-3">
            <p class="text-muted mb-1"><small>{{ card.label }}</small></p>
            <h5 class="mb-0 font-weight-bold" :class="card.cls">{{ card.value }}</h5>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="row mb-3">
      <div class="col-md-4 mb-2">
        <input
          v-model="search"
          class="form-control"
          placeholder="Search reference, sender, amount…"
          @input="debouncedLoad"
        />
      </div>
      <div class="col-md-2 mb-2">
        <select v-model="filterStatus" class="form-control" @change="load()">
          <option value="">All statuses</option>
          <option value="unassigned">Unassigned</option>
          <option value="pending_match">Suggested Match</option>
          <option value="confirmed">Validated</option>
          <option value="invalidated">Invalidated</option>
        </select>
      </div>
      <div class="col-md-2 mb-2">
        <select v-model="filterMethod" class="form-control" @change="load()">
          <option value="">All methods</option>
          <option value="sms">SMS (all providers)</option>
          <option value="stk">M-Pesa Prompt</option>
          <option value="c2b">Paybill (C2B)</option>
          <option value="manual">Manual</option>
          <option value="reference_code">By Reference</option>
        </select>
      </div>
      <div class="col-md-4 mb-2">
        <date-range-picker
          :auto-apply="true"
          v-model="dateRange"
          :ranges="false"
          opens="right"
          :append-to-body="true"
          class="w-100"
          @update="load()"
        >
          <template #input="picker">
            <div class="form-control" style="cursor:pointer">
              <i class="bi bi-calendar3 mr-1"></i>
              <span v-if="picker.startDate">
                {{ fmt(picker.startDate, 'DD MMM') }} – {{ fmt(picker.endDate, 'DD MMM YYYY') }}
              </span>
              <span v-else class="text-muted">Filter by date</span>
            </div>
          </template>
        </date-range-picker>
      </div>
      <div class="col-md-2 mb-2 d-flex align-items-center">
        <a href="#" class="text-muted" @click.prevent="clearFilters">Clear filters</a>
      </div>
    </div>

    <!-- Table -->
    <div class="mb-5">
      <b-table
        striped
        hover
        sort-icon-left
        responsive="sm"
        sticky-header="80vh"
        :items="transactions"
        :fields="tableFields"
        :busy="loading"
        show-empty
        @row-clicked="openDetail"
      >
        <template #table-busy>
          <div class="text-center py-5">
            <div class="spinner-border text-secondary" role="status">
              <span class="sr-only">Loading…</span>
            </div>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-5 text-muted">
            <i class="bi bi-inbox" style="font-size:2rem"></i>
            <p class="mt-2 mb-0">No transactions found</p>
          </div>
        </template>

        <template #cell(created_at)="{ item }">
          {{ fmt(item.created_at, 'DD MMM YY HH:mm') }}
        </template>

        <template #cell(transaction_code)="{ item }">
          <strong>{{ item.transaction_code }}</strong>
        </template>

        <template #cell(amount)="{ item }">
          <span class="float-right">{{ formatAmount(item.amount) }}</span>
        </template>

        <template #cell(provider)="{ item }">
          <span :class="['badge', providerClass(item.provider)]">{{ item.provider || '—' }}</span>
        </template>

        <template #cell(assignment_status)="{ item }">
          <span :class="['badge', statusClass(item.assignment_status)]">
            {{ statusLabel(item.assignment_status) }}
          </span>
        </template>

        <template #cell(assignable)="{ item }">
          <span v-if="item.assignable">
            <span class="badge badge-dark mr-1">{{ recordTypeLabel(item.assignable_type) }}</span>
            #{{ item.assignable.order || item.assignable.id.slice(-6) }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(actions)="{ item }">
          <button class="btn btn-outline-secondary btn-sm" @click.stop="openDetail(item)">
            <i class="bi bi-eye"></i>
          </button>
        </template>
      </b-table>

      <!-- Pagination -->
      <nav v-if="pagination.last_page > 1" class="d-flex justify-content-center mt-3">
        <ul class="pagination pagination-sm">
          <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
            <a class="page-link" href="#" @click.prevent="goPage(pagination.current_page - 1)">
              <i class="bi bi-chevron-left"></i>
            </a>
          </li>
          <li class="page-item disabled">
            <span class="page-link">
              Page {{ pagination.current_page }} of {{ pagination.last_page }}
            </span>
          </li>
          <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
            <a class="page-link" href="#" @click.prevent="goPage(pagination.current_page + 1)">
              <i class="bi bi-chevron-right"></i>
            </a>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Detail modal -->
    <b-modal
      ref="detailModal"
      size="lg"
      hide-footer
      :title="detailTx ? 'Transaction: ' + detailTx.transaction_code : 'Transaction Detail'"
    >
      <div v-if="detailTx">

        <!-- Core fields -->
        <div class="row mb-3">
          <div class="col-6 mb-2">
            <p class="text-muted mb-0"><small>Reference</small></p>
            <strong>{{ detailTx.transaction_code }}</strong>
          </div>
          <div class="col-6 mb-2">
            <p class="text-muted mb-0"><small>Amount</small></p>
            <strong>{{ formatAmount(detailTx.amount) }}</strong>
          </div>
          <div class="col-6 mb-2">
            <p class="text-muted mb-0"><small>Sender</small></p>
            <span>{{ detailTx.customer || '—' }}</span>
          </div>
          <div class="col-6 mb-2">
            <p class="text-muted mb-0"><small>Received At</small></p>
            <span>{{ fmt(detailTx.created_at, 'DD MMM YYYY HH:mm') }}</span>
          </div>
          <div class="col-6 mb-2">
            <p class="text-muted mb-0"><small>Status</small></p>
            <span :class="['badge', statusClass(detailTx.assignment_status)]">
              {{ statusLabel(detailTx.assignment_status) }}
            </span>
          </div>
          <div class="col-6 mb-2">
            <p class="text-muted mb-0"><small>Source</small></p>
            <span class="text-capitalize">
              {{ detailTx.validation_method === 'sms' ? 'SMS Validator App' : (detailTx.validation_method === 'stk' ? 'M-Pesa Prompt' : (detailTx.validation_method === 'c2b' ? 'Paybill (C2B)' : (detailTx.validation_method || '—'))) }}
            </span>
          </div>
        </div>

        <!-- Original SMS -->
        <div v-if="detailTx.sms_message && detailTx.sms_message.message" class="mb-3">
          <p class="font-weight-bold text-muted mb-1"><small>Original SMS</small></p>
          <div class="border rounded p-2 bg-light">
            <span>{{ detailTx.sms_message.message }}</span>
          </div>
        </div>

        <!-- Assignment -->
        <div v-if="detailTx.assignable" class="mb-3">
          <p class="font-weight-bold text-muted mb-1"><small>Assigned To</small></p>
          <p class="mb-0">
            <span class="badge badge-dark mr-1">{{ recordTypeLabel(detailTx.assignable_type) }}</span>
            <strong>#{{ detailTx.assignable.order || detailTx.assignable.id }}</strong>
            <span v-if="detailTx.assigned_by_user" class="text-muted ml-2">
              by {{ detailTx.assigned_by_user.name }}
            </span>
            <span v-if="detailTx.assigned_at" class="text-muted ml-1">
              at {{ fmt(detailTx.assigned_at, 'DD MMM HH:mm') }}
            </span>
          </p>
        </div>

        <!-- Payment Allocation panel -->
        <div
          v-if="canManageAssignments && detailTx.assignment_status !== 'invalidated'"
          class="mb-3"
        >
          <div class="d-flex justify-content-between align-items-center mb-2">
            <p class="font-weight-bold text-muted mb-0"><small>Payment Allocation</small></p>
            <button class="btn btn-primary btn-sm" @click="openSplitAlloc">
              <i class="bi bi-scissors"></i> Allocate Payment
            </button>
          </div>

          <!-- Existing allocation legs -->
          <div v-if="allocState.legs && allocState.legs.length" class="border rounded p-2 bg-light">
            <div v-for="(leg, i) in allocState.legs" :key="i" class="d-flex justify-content-between align-items-center mb-1">
              <span>
                <span :class="['badge', 'mr-1', leg.type === 'wallet' ? 'badge-primary' : leg.type === 'reservation' ? 'badge-dark' : 'badge-dark']">
                  {{ leg.type }}
                </span>
                <small>{{ leg.payable_label || leg.type }}</small>
              </span>
              <strong class="text-primary">+ {{ formatAmount(leg.amount) }}</strong>
            </div>
            <hr class="my-1"/>
            <div class="d-flex justify-content-between">
              <small class="text-muted">Allocated</small>
              <small class="font-weight-bold">{{ formatAmount(allocState.allocated) }} / {{ formatAmount(detailTx.amount) }}</small>
            </div>
            <div v-if="allocState.unallocated > 0.009" class="d-flex justify-content-between">
              <small class="text-dark">Unallocated</small>
              <small class="text-dark font-weight-bold">{{ formatAmount(allocState.unallocated) }}</small>
            </div>
          </div>
          <p v-else class="text-muted mb-0"><small>No allocations yet for this transaction.</small></p>
        </div>

        <!-- Audit trail -->
        <div v-if="detailTx.audit_logs && detailTx.audit_logs.length" class="mb-3">
          <p class="font-weight-bold text-muted mb-2"><small>Audit Trail</small></p>
          <ul class="list-unstyled mb-0">
            <li v-for="(log, i) in detailTx.audit_logs" :key="i" class="d-flex mb-3">
              <div class="mr-3 text-center" style="width:24px;flex-shrink:0">
                <i :class="auditIcon(log.action)"></i>
              </div>
              <div>
                <p class="mb-0 font-weight-bold text-capitalize">{{ auditLabel(log.action) }}</p>
                <p class="mb-0 text-muted">
                  <small>{{ fmt(log.created_at, 'DD MMM YYYY HH:mm') }}</small>
                  <span v-if="log.user"><small> · {{ log.user.name }}</small></span>
                </p>
                <p v-if="log.meta && log.meta.reason" class="mb-0 text-muted">
                  <small><em>"{{ log.meta.reason }}"</em></small>
                </p>
              </div>
            </li>
          </ul>
        </div>

        <!-- Assignment history -->
        <div v-if="detailTx.assignment_history && detailTx.assignment_history.length" class="mb-3">
          <p class="font-weight-bold text-muted mb-2"><small>Assignment History</small></p>
          <ul class="list-unstyled mb-0">
            <li v-for="(h, i) in detailTx.assignment_history" :key="i" class="mb-1">
              <span class="badge badge-dark text-capitalize mr-1">{{ h.action }}</span>
              <span v-if="h.to_assignable_type">→ {{ recordTypeLabel(h.to_assignable_type) }}</span>
              <span v-if="h.performed_by" class="text-muted ml-1">by {{ h.performed_by.name }}</span>
              <span v-if="h.reason" class="text-muted ml-1"><em>"{{ h.reason }}"</em></span>
            </li>
          </ul>
        </div>

        <!-- Admin actions -->
        <div v-if="canManageAssignments || canSkipValidation" class="border-top pt-3 mt-2">
          <p class="font-weight-bold text-muted mb-2"><small>Administrative Actions</small></p>
          <button
            v-if="canManageAssignments && detailTx.assignable"
            class="btn btn-outline-dark btn-sm mr-2 mb-2"
            @click="openUnassign"
          >
            <i class="bi bi-x-circle"></i> Unassign
          </button>
          <button
            v-if="canManageAssignments && detailTx.assignable"
            class="btn btn-outline-primary btn-sm mr-2 mb-2"
            @click="openReassign"
          >
            <i class="bi bi-arrow-left-right"></i> Reassign
          </button>
          <button
            v-if="canManageAssignments && detailTx.assignment_status !== 'invalidated'"
            class="btn btn-outline-danger btn-sm mr-2 mb-2"
            @click="openInvalidate"
          >
            <i class="bi bi-slash-circle"></i> Invalidate
          </button>
          <button
            v-if="canSkipValidation && !detailTx.assignable"
            class="btn btn-outline-primary btn-sm mb-2"
            @click="openForceValidate"
          >
            <i class="bi bi-check-circle"></i> Force Validate
          </button>
        </div>

      </div>
    </b-modal>

    <!-- Unassign modal -->
    <b-modal ref="unassignModal" title="Unassign Transaction" hide-footer>
      <p>Remove this transaction's assignment. The linked record will revert to pending validation.</p>
      <div class="form-group">
        <label>Reason <span class="text-danger">*</span></label>
        <textarea
          v-model="actionForm.reason"
          class="form-control"
          rows="3"
          placeholder="Why are you unassigning this transaction?"
        ></textarea>
      </div>
      <div class="d-flex justify-content-end">
        <button class="btn btn-secondary mr-2" @click="$refs.unassignModal.hide()">Cancel</button>
        <button
          class="btn btn-dark"
          :disabled="actionForm.reason.length < 5 || actionForm.loading"
          @click="submitUnassign"
        >
          <span v-if="actionForm.loading" class="spinner-border spinner-border-sm mr-1"></span>
          Unassign
        </button>
      </div>
      <p v-if="actionForm.error" class="text-danger mt-2 mb-0">{{ actionForm.error }}</p>
    </b-modal>

    <!-- Reassign modal -->
    <b-modal ref="reassignModal" title="Reassign Transaction" hide-footer>
      <p>Move this transaction to a different order, reservation, or subscription.</p>
      <div class="form-group">
        <label>Record Type</label>
        <select v-model="reassignForm.record_type" class="form-control">
          <option value="payment">Payment</option>
          <option value="order">Order</option>
          <option value="reservation">Reservation</option>
          <option value="subscription">Subscription</option>
        </select>
      </div>
      <div class="form-group">
        <label>Record ID <span class="text-danger">*</span></label>
        <input v-model="reassignForm.record_id" class="form-control" placeholder="Paste the record UUID" />
      </div>
      <div class="form-group">
        <label>Reason <span class="text-danger">*</span></label>
        <textarea v-model="reassignForm.reason" class="form-control" rows="2" placeholder="Why are you reassigning?"></textarea>
      </div>
      <div class="d-flex justify-content-end">
        <button class="btn btn-secondary mr-2" @click="$refs.reassignModal.hide()">Cancel</button>
        <button
          class="btn btn-primary"
          :disabled="!reassignForm.record_id || reassignForm.reason.length < 5 || reassignForm.loading"
          @click="submitReassign"
        >
          <span v-if="reassignForm.loading" class="spinner-border spinner-border-sm mr-1"></span>
          Reassign
        </button>
      </div>
      <p v-if="reassignForm.error" class="text-danger mt-2 mb-0">{{ reassignForm.error }}</p>
    </b-modal>

    <!-- Invalidate modal -->
    <b-modal ref="invalidateModal" title="Invalidate Transaction" hide-footer>
      <p class="text-danger"><i class="bi bi-exclamation-triangle"></i> This marks the transaction as invalid. Records are never deleted.</p>
      <div class="form-group">
        <label>Type</label>
        <select v-model="invalidateForm.type" class="form-control">
          <option value="invalid">Invalid</option>
          <option value="fraudulent">Fraudulent</option>
          <option value="duplicate">Duplicate</option>
          <option value="mistaken_assignment">Mistaken Assignment</option>
        </select>
      </div>
      <div class="form-group">
        <label>Reason <span class="text-danger">*</span></label>
        <textarea
          v-model="invalidateForm.reason"
          class="form-control"
          rows="3"
          placeholder="Explain why this transaction is being invalidated…"
        ></textarea>
      </div>
      <div class="d-flex justify-content-end">
        <button class="btn btn-secondary mr-2" @click="$refs.invalidateModal.hide()">Cancel</button>
        <button
          class="btn btn-danger"
          :disabled="invalidateForm.reason.length < 5 || invalidateForm.loading"
          @click="submitInvalidate"
        >
          <span v-if="invalidateForm.loading" class="spinner-border spinner-border-sm mr-1"></span>
          Invalidate
        </button>
      </div>
      <p v-if="invalidateForm.error" class="text-danger mt-2 mb-0">{{ invalidateForm.error }}</p>
    </b-modal>

    <!-- Split Allocation modal -->
    <b-modal ref="splitAllocModal" title="Allocate Payment" size="lg" hide-footer>
      <div v-if="detailTx">

        <!-- Transaction summary bar -->
        <div class="d-flex justify-content-between align-items-center p-2 rounded mb-3" style="background:#f8f9fa;border:1px solid #dee2e6">
          <span>
            <strong>{{ detailTx.transaction_code }}</strong>
            <span class="text-muted ml-2">{{ detailTx.customer || '' }}</span>
          </span>
          <div class="text-right">
            <div><small class="text-muted">Total received: </small><strong>{{ formatAmount(detailTx.amount) }}</strong></div>
            <div>
              <small class="text-muted">Still to allocate: </small>
              <strong :class="splitAllocRemainder > 0.009 ? 'text-danger' : 'text-primary'">
                {{ formatAmount(splitAllocRemainder) }}
              </strong>
            </div>
          </div>
        </div>

        <!-- Existing allocations from DB -->
        <div v-if="allocState.legs && allocState.legs.length" class="mb-3">
          <p class="text-muted mb-1"><small>Previously allocated</small></p>
          <div v-for="(leg, i) in allocState.legs" :key="'existing-' + i"
            class="d-flex justify-content-between align-items-center p-2 border rounded mb-1 bg-white">
            <span>
              <span :class="['badge', 'mr-2', leg.type === 'wallet' ? 'badge-primary' : leg.type === 'reservation' ? 'badge-dark' : 'badge-dark']">
                {{ leg.type }}
              </span>
              <small>{{ leg.payable_label }}</small>
            </span>
            <span class="text-primary font-weight-bold">{{ formatAmount(leg.amount) }}</span>
          </div>
        </div>

        <!-- Add new leg form -->
        <div class="border rounded p-3 mb-3" style="background:#f8f9fa">

          <!-- Type selector as button group -->
          <div class="btn-group btn-group-sm mb-3 w-100" role="group">
            <button
              type="button"
              :class="['btn', splitAlloc.newLeg.type === 'order' ? 'btn-dark' : 'btn-outline-dark']"
              @click="setLegType('order')"
            ><i class="bi bi-receipt mr-1"></i>Order</button>
            <button
              v-if="hasReservations"
              type="button"
              :class="['btn', splitAlloc.newLeg.type === 'reservation' ? 'btn-dark' : 'btn-outline-dark']"
              @click="setLegType('reservation')"
            ><i class="bi bi-calendar-check mr-1"></i>Reservation</button>
            <button
              v-if="walletEnabled"
              type="button"
              :class="['btn', splitAlloc.newLeg.type === 'wallet' ? 'btn-primary' : 'btn-outline-primary']"
              @click="setLegType('wallet')"
            ><i class="bi bi-wallet2 mr-1"></i>Load to Wallet</button>
          </div>

          <!-- Search box -->
          <div class="position-relative mb-2">
            <div class="input-group input-group-sm">
              <div class="input-group-prepend">
                <span class="input-group-text"><i class="bi bi-search"></i></span>
              </div>
              <input
                v-model="splitAlloc.search"
                class="form-control"
                :placeholder="splitAlloc.newLeg.type === 'wallet'
                  ? 'Search customer by name or phone…'
                  : splitAlloc.newLeg.type === 'reservation'
                    ? 'Search reservation by number or guest name…'
                    : 'Search order by number or customer name…'"
                @input="debouncedAllocSearch"
                @keydown.esc="splitAlloc.searchResults = []"
                autocomplete="off"
              />
              <div class="input-group-append" v-if="splitAlloc.searchLoading">
                <span class="input-group-text"><span class="spinner-border spinner-border-sm"></span></span>
              </div>
            </div>

            <!-- Search results dropdown -->
            <div
              v-if="splitAlloc.searchResults.length"
              class="border rounded bg-white shadow-sm position-absolute w-100"
              style="z-index:1050;top:100%;max-height:200px;overflow-y:auto"
            >
              <div
                v-for="r in splitAlloc.searchResults"
                :key="r.id"
                class="px-3 py-2 border-bottom"
                style="cursor:pointer"
                @mousedown.prevent="selectAllocTarget(r)"
              >
                <small>{{ r.label }}</small>
              </div>
            </div>
          </div>

          <!-- Selected target confirmation -->
          <div v-if="splitAlloc.newLeg.target" class="mb-2">
            <span class="badge badge-dark">
              <i class="bi bi-check-circle mr-1"></i>
              {{ splitAlloc.newLeg.label }}
            </span>
            <a href="#" class="text-muted ml-2" style="font-size:0.75rem" @click.prevent="clearAllocTarget">change</a>
          </div>

          <!-- Amount row -->
          <div class="d-flex align-items-center mt-2">
            <input
              v-model="splitAlloc.newLeg.amount"
              type="number"
              min="0.01"
              :max="splitAllocRemainder"
              step="0.01"
              class="form-control form-control-sm mr-2"
              placeholder="Amount (KES)"
              style="max-width:180px"
              :disabled="!splitAlloc.newLeg.target"
            />
            <a
              v-if="splitAllocRemainder > 0.009 && splitAlloc.newLeg.target"
              href="#" class="text-primary mr-3" style="font-size:0.8rem;white-space:nowrap"
              @click.prevent="splitAlloc.newLeg.amount = splitAllocRemainder"
            >Use {{ formatAmount(splitAllocRemainder) }}</a>
            <button
              class="btn btn-primary btn-sm ml-auto"
              :disabled="!splitAlloc.newLeg.target || !splitAlloc.newLeg.amount || splitAllocRemainder <= 0 || splitAlloc.loading"
              @click="submitSingleLeg"
            >
              <span v-if="splitAlloc.loading" class="spinner-border spinner-border-sm mr-1"></span>
              Allocate
            </button>
          </div>
        </div>

        <div v-if="splitAlloc.error" class="alert alert-danger py-2 mb-2">{{ splitAlloc.error }}</div>
        <div v-if="splitAlloc.success" class="alert alert-primary py-2 mb-2">{{ splitAlloc.success }}</div>

        <div class="d-flex justify-content-end">
          <button class="btn btn-dark" @click="$refs.splitAllocModal.hide()">Close</button>
        </div>

      </div>
    </b-modal>

    <!-- Force validate modal -->
    <b-modal ref="forceValidateModal" title="Force Validate" hide-footer>
      <p class="text-danger"><i class="bi bi-exclamation-triangle"></i> Force validation bypasses normal workflow. This action is fully audited.</p>
      <div class="form-group">
        <label>Record Type</label>
        <select v-model="forceForm.record_type" class="form-control">
          <option value="payment">Payment</option>
          <option value="order">Order</option>
          <option value="reservation">Reservation</option>
          <option value="subscription">Subscription</option>
        </select>
      </div>
      <div class="form-group">
        <label>Record ID <span class="text-danger">*</span></label>
        <input v-model="forceForm.record_id" class="form-control" placeholder="Paste the record UUID" />
      </div>
      <div class="form-group">
        <label>Reason <span class="text-danger">*</span></label>
        <textarea v-model="forceForm.reason" class="form-control" rows="2" placeholder="Reason for forced validation…"></textarea>
      </div>
      <div class="d-flex justify-content-end">
        <button class="btn btn-secondary mr-2" @click="$refs.forceValidateModal.hide()">Cancel</button>
        <button
          class="btn btn-primary"
          :disabled="!forceForm.record_id || forceForm.reason.length < 5 || forceForm.loading"
          @click="submitForceValidate"
        >
          <span v-if="forceForm.loading" class="spinner-border spinner-border-sm mr-1"></span>
          Force Validate
        </button>
      </div>
      <p v-if="forceForm.error" class="text-danger mt-2 mb-0">{{ forceForm.error }}</p>
    </b-modal>

  </div>
  </template>

  <div class="container-fluid" v-if="activeTab === 'ledger'">
    <ledger-transactions></ledger-transactions>
  </div>
 </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';

export default {
  name: 'PaymentTransactions',

  components: { DateRangePicker },

  props: {
    initialTab: { type: String, default: '' },
  },

  data() {
    return {
      activeTab: this.initialTab === 'ledger' ? 'ledger' : 'mpesa',
      loading: false,
      mpesaConfig: false,
      transactions: [],
      tableFields: [
        { key: 'created_at',       label: 'Date / Time',  sortable: true  },
        { key: 'transaction_code', label: 'Reference',     sortable: true  },
        { key: 'customer',         label: 'Sender',        sortable: true  },
        { key: 'amount',           label: 'Amount',        sortable: true  },
        { key: 'provider',         label: 'Provider',      sortable: false },
        { key: 'assignment_status',label: 'Status',        sortable: true  },
        { key: 'assignable',       label: 'Assigned To',   sortable: false },
        { key: 'actions',          label: '',              sortable: false },
      ],
      summary: { total: 0, validated: 0, unassigned: 0, invalidated: 0, validated_value: 0 },
      pagination: { current_page: 1, last_page: 1 },
      search: '',
      filterStatus: '',
      filterMethod: '',
      dateRange: { startDate: null, endDate: null },
      searchTimer: null,
      detailTx: null,
      actionForm:     { reason: '', loading: false, error: '' },
      reassignForm:   { record_type: 'payment', record_id: '', reason: '', loading: false, error: '' },
      invalidateForm: { type: 'invalid', reason: '', loading: false, error: '' },
      forceForm:      { record_type: 'payment', record_id: '', reason: '', loading: false, error: '' },
      allocateForm:   { customer_id: '', amount: '', loading: false, error: '', success: '' },
      allocState:     { transaction_amount: 0, allocated: 0, unallocated: 0, legs: [] },
      splitAlloc:     {
        newLeg: { type: 'order', target: '', label: '', amount: '' },
        search: '',
        searchResults: [],
        searchLoading: false,
        searchTimer: null,
        loading: false,
        error: '',
        success: '',
      },
    };
  },

  computed: {
    ...mapGetters(['userDetails']),

    walletEnabled() {
      return !!this.userDetails?.company?.companysettings?.enable_customer_wallet;
    },

    hasReservations() {
      return !!this.userDetails?.company?.companysettings?.has_reservations;
    },

    canManageAssignments() {
      const perms = this.userDetails?.permissions || [];
      return this.userDetails?.roles?.[0]?.name === 'Admin' ||
             perms.some(p => p.name === 'manage_payment_assignments');
    },

    canSkipValidation() {
      const perms = this.userDetails?.permissions || [];
      return this.userDetails?.roles?.[0]?.name === 'Admin' ||
             perms.some(p => p.name === 'skip_payment_validation');
    },

    splitAllocRemainder() {
      if (!this.detailTx) return 0;
      const txAmount    = parseFloat(this.detailTx.amount) || 0;
      const dbAllocated = parseFloat(this.allocState.allocated) || 0;
      return Math.max(0, txAmount - dbAllocated);
    },

    summaryCards() {
      const s = this.summary;
      return [
        { label: 'Total Transactions', value: s.total       || 0,                           cls: '' },
        { label: 'Validated',          value: s.validated   || 0,                           cls: 'text-primary' },
        { label: 'Unassigned',         value: s.unassigned  || 0,                           cls: 'text-dark' },
        { label: 'Validated Value',    value: this.formatAmount(s.validated_value || 0),    cls: 'text-primary' },
        { label: 'Invalidated',        value: s.invalidated || 0,                           cls: 'text-danger' },
      ];
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    setTab(tab) {
      this.activeTab = tab;
      const path = tab === 'ledger' ? '/payment-transactions/ledger' : '/payment-transactions/sms-sync';
      if (window.location.pathname !== path) {
        window.history.replaceState(null, '', path);
      }
    },

    async load(page = 1) {
      this.loading = true;
      try {
        const params = { page };
        if (this.search)              params.search     = this.search;
        if (this.filterStatus)        params.status     = this.filterStatus;
        if (this.filterMethod)        params.method     = this.filterMethod;
        if (this.dateRange.startDate) params.start_date = this.dateRange.startDate.toISOString().slice(0, 10);
        if (this.dateRange.endDate)   params.end_date   = this.dateRange.endDate.toISOString().slice(0, 10);

        const { data } = await axios.get('/api/payment-validation/transactions', { params });

        this.transactions = data.transactions?.data || [];
        this.pagination   = {
          current_page: data.transactions?.current_page || 1,
          last_page:    data.transactions?.last_page    || 1,
        };
        this.summary = data.summary || {};
      } catch {
        // silent
      } finally {
        this.loading = false;
      }
    },

    goPage(page) {
      this.load(page);
    },

    debouncedLoad() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.load(), 400);
    },

    clearFilters() {
      this.search = '';
      this.filterStatus = '';
      this.filterMethod = '';
      this.dateRange = { startDate: null, endDate: null };
      this.load();
    },

    async openDetail(tx) {
      this.detailTx   = tx;
      this.allocState = { transaction_amount: 0, allocated: 0, unallocated: 0, legs: [] };
      this.$refs.detailModal.show();
      try {
        const [detailRes, allocRes] = await Promise.all([
          axios.get(`/api/payment-validation/transactions/${tx.id}`),
          axios.get(`/api/payments/allocation-state/${tx.id}`),
        ]);
        this.detailTx   = detailRes.data;
        this.allocState = allocRes.data;
      } catch { /* keep partial data already shown */ }
    },

    openUnassign() {
      this.actionForm = { reason: '', loading: false, error: '' };
      this.$refs.unassignModal.show();
    },

    openReassign() {
      this.reassignForm = { record_type: 'payment', record_id: '', reason: '', loading: false, error: '' };
      this.$refs.reassignModal.show();
    },

    openInvalidate() {
      this.invalidateForm = { type: 'invalid', reason: '', loading: false, error: '' };
      this.$refs.invalidateModal.show();
    },

    openForceValidate() {
      this.forceForm = { record_type: 'payment', record_id: '', reason: '', loading: false, error: '' };
      this.$refs.forceValidateModal.show();
    },

    async submitUnassign() {
      this.actionForm.loading = true;
      this.actionForm.error   = '';
      try {
        await axios.delete('/api/payment-assignments/remove', {
          data: { transactions_validated_id: this.detailTx.id, reason: this.actionForm.reason },
        });
        this.$refs.unassignModal.hide();
        this.$refs.detailModal.hide();
        this.load();
      } catch (e) {
        this.actionForm.error = e.response?.data?.message || 'Failed to unassign.';
      } finally {
        this.actionForm.loading = false;
      }
    },

    async submitReassign() {
      this.reassignForm.loading = true;
      this.reassignForm.error   = '';
      try {
        await axios.post('/api/payment-assignments/reassign', {
          transactions_validated_id: this.detailTx.id,
          record_type: this.reassignForm.record_type,
          record_id:   this.reassignForm.record_id,
          reason:      this.reassignForm.reason,
        });
        this.$refs.reassignModal.hide();
        this.$refs.detailModal.hide();
        this.load();
      } catch (e) {
        this.reassignForm.error = e.response?.data?.message || 'Failed to reassign.';
      } finally {
        this.reassignForm.loading = false;
      }
    },

    async submitInvalidate() {
      this.invalidateForm.loading = true;
      this.invalidateForm.error   = '';
      try {
        await axios.post(`/api/payment-validation/transactions/${this.detailTx.id}/invalidate`, {
          reason: this.invalidateForm.reason,
          type:   this.invalidateForm.type,
        });
        this.$refs.invalidateModal.hide();
        this.$refs.detailModal.hide();
        this.load();
      } catch (e) {
        this.invalidateForm.error = e.response?.data?.message || 'Failed to invalidate.';
      } finally {
        this.invalidateForm.loading = false;
      }
    },

    async submitForceValidate() {
      this.forceForm.loading = true;
      this.forceForm.error   = '';
      try {
        await axios.post(`/api/payment-validation/transactions/${this.detailTx.id}/force-validate`, {
          record_type: this.forceForm.record_type,
          record_id:   this.forceForm.record_id,
          reason:      this.forceForm.reason,
        });
        this.$refs.forceValidateModal.hide();
        this.$refs.detailModal.hide();
        this.load();
      } catch (e) {
        this.forceForm.error = e.response?.data?.message || 'Failed to force validate.';
      } finally {
        this.forceForm.loading = false;
      }
    },

    openSplitAlloc() {
      this.splitAlloc = {
        newLeg: { type: 'order', target: '', label: '', amount: '' },
        search: '',
        searchResults: [],
        searchLoading: false,
        searchTimer: null,
        loading: false,
        error: '',
        success: '',
      };
      this.$refs.splitAllocModal.show();
    },

    setLegType(type) {
      this.splitAlloc.newLeg.type = type;
      this.clearAllocTarget();
    },

    clearAllocTarget() {
      this.splitAlloc.newLeg.target = '';
      this.splitAlloc.newLeg.label  = '';
      this.splitAlloc.newLeg.amount = '';
      this.splitAlloc.search        = '';
      this.splitAlloc.searchResults = [];
    },

    debouncedAllocSearch() {
      clearTimeout(this.splitAlloc.searchTimer);
      this.splitAlloc.searchTimer = setTimeout(() => this.searchAllocTarget(), 350);
    },

    async searchAllocTarget() {
      const q = this.splitAlloc.search?.trim();
      if (!q || q.length < 2) { this.splitAlloc.searchResults = []; return; }

      this.splitAlloc.searchLoading = true;
      try {
        const type = this.splitAlloc.newLeg.type;
        if (type === 'order') {
          const { data } = await axios.get('/api/allorders', { params: { search: q, per_page: 8 } });
          const items = data.data || data.orders?.data || [];
          this.splitAlloc.searchResults = items.map(o => ({
            id:    o.id,
            label: `Order #${o.order}${o.customer?.full_name ? ' — ' + o.customer.full_name : ''} — Balance: ${this.formatAmount(o.balance)}`,
          }));
        } else if (type === 'reservation') {
          const { data } = await axios.get('/api/reservations', { params: { search: q, per_page: 8 } });
          const items = data.data || data.reservations?.data || [];
          this.splitAlloc.searchResults = items.map(r => ({
            id:    r.id,
            label: `#${r.reservation_number}${r.customer?.full_name ? ' — ' + r.customer.full_name : ''} — Balance: ${this.formatAmount(r.balance)}`,
          }));
        } else if (type === 'wallet') {
          const { data } = await axios.get('/api/allcustomers', { params: { search: q, perPage: 8 } });
          const items = data.data || [];
          this.splitAlloc.searchResults = items.map(c => ({
            id:    c.id,
            label: `${c.full_name}${c.phone ? ' — ' + c.phone : ''}${c.email ? ' — ' + c.email : ''}`,
          }));
        }
      } catch {
        this.splitAlloc.searchResults = [];
      } finally {
        this.splitAlloc.searchLoading = false;
      }
    },

    selectAllocTarget(result) {
      this.splitAlloc.newLeg.target = result.id;
      this.splitAlloc.newLeg.label  = result.label;
      this.splitAlloc.search        = '';
      this.splitAlloc.searchResults = [];
    },

    async submitSingleLeg() {
      const amount = parseFloat(this.splitAlloc.newLeg.amount);
      if (!this.splitAlloc.newLeg.target || !amount || amount <= 0) return;
      if (amount > this.splitAllocRemainder + 0.01) {
        this.splitAlloc.error = `Amount (${this.formatAmount(amount)}) exceeds remaining balance (${this.formatAmount(this.splitAllocRemainder)}).`;
        return;
      }
      this.splitAlloc.loading = true;
      this.splitAlloc.error   = '';
      this.splitAlloc.success = '';
      try {
        const { data } = await axios.post('/api/payments/split-allocate', {
          transactions_validated_id: this.detailTx.id,
          legs: [{ type: this.splitAlloc.newLeg.type, target: this.splitAlloc.newLeg.target, amount }],
        });
        const allocRes = await axios.get(`/api/payments/allocation-state/${this.detailTx.id}`);
        this.allocState = allocRes.data;
        this.splitAlloc.success = `Allocated ${this.formatAmount(amount)}. Remaining: ${this.formatAmount(data.unallocated)}.`;
        const prevType = this.splitAlloc.newLeg.type;
        this.clearAllocTarget();
        this.splitAlloc.newLeg.type = prevType;
        this.load();
      } catch (e) {
        this.splitAlloc.error = e.response?.data?.message || 'Failed to allocate.';
      } finally {
        this.splitAlloc.loading = false;
      }
    },

    exportCSV() {
      if (!this.transactions.length) return;
      const headers = ['Reference', 'Sender', 'Amount', 'Status', 'Assigned To', 'Date'];
      const rows = this.transactions.map(tx => [
        tx.transaction_code,
        tx.customer || '',
        tx.amount,
        this.statusLabel(tx.assignment_status),
        tx.assignable ? this.recordTypeLabel(tx.assignable_type) + ' #' + (tx.assignable.order || tx.assignable.id.slice(-6)) : '',
        this.fmt(tx.created_at, 'YYYY-MM-DD HH:mm'),
      ].map(v => JSON.stringify(v)).join(','));
      const csv  = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = 'payment_transactions.csv';
      a.click();
      URL.revokeObjectURL(url);
    },

    statusClass(s) {
      const map = {
        confirmed:     'badge-primary',
        pending_match: 'badge-dark',
        unassigned:    'badge-dark',
        invalidated:   'badge-danger',
      };
      return map[s] || 'badge-dark';
    },

    statusLabel(s) {
      const map = {
        confirmed:     'Validated',
        pending_match: 'Suggested Match',
        unassigned:    'Unassigned',
        invalidated:   'Invalidated',
      };
      return map[s] || s || 'Unassigned';
    },

    methodLabel(m) {
      const map = { sms: 'SMS', stk: 'M-Pesa Prompt', c2b: 'Paybill (C2B)', manual: 'Manual', reference_code: 'By Reference' };
      return map[m] || m || '—';
    },

    methodClass(m) {
      const map = { sms: 'badge-primary', stk: 'badge-primary', c2b: 'badge-primary', manual: 'badge-dark', reference_code: 'badge-dark' };
      return map[m] || 'badge-dark';
    },

    providerClass(sender) {
      if (!sender) return 'badge-dark';
      const s = sender.toUpperCase();
      if (s === 'MPESA')  return 'badge-primary';
      if (s === 'KCB')    return 'badge-primary';
      if (s === 'AIRTEL') return 'badge-danger';
      if (s === 'EQUITY') return 'badge-dark';
      return 'badge-dark';
    },

    recordTypeLabel(type) {
      if (!type) return '';
      if (type.includes('FinalOrder'))   return 'Order';
      if (type.includes('Reservation'))  return 'Reservation';
      if (type.includes('Subscription')) return 'Subscription';
      if (type.includes('Payment'))      return 'Payment';
      return type.split('\\').pop();
    },

    auditLabel(action) {
      const map = {
        validated:          'Payment Validated',
        force_validated:    'Force Validated',
        invalidated:        'Invalidated',
        override_requested: 'Override Requested',
        override_approved:  'Override Approved',
        match_suggested:    'Match Suggested',
        assigned:           'Assigned',
        reassigned:         'Reassigned',
        removed:            'Unassigned',
        reference_code:     'Validated by Reference',
      };
      return map[action] || action;
    },

    auditIcon(action) {
      const map = {
        validated:          'bi bi-check-circle-fill text-primary',
        force_validated:    'bi bi-shield-check text-primary',
        invalidated:        'bi bi-slash-circle text-danger',
        override_requested: 'bi bi-exclamation-triangle text-danger',
        override_approved:  'bi bi-patch-check text-primary',
        match_suggested:    'bi bi-diagram-2 text-muted',
        assigned:           'bi bi-link text-primary',
        reassigned:         'bi bi-arrow-left-right text-primary',
        removed:            'bi bi-x-circle text-dark',
      };
      return map[action] || 'bi bi-dot text-muted';
    },

    formatAmount(n) {
      if (this.$root?.convertCurrencyToLocal) return this.$root.convertCurrencyToLocal(n);
      return n ? Number(n).toLocaleString('en-KE', { minimumFractionDigits: 2 }) : '0.00';
    },

    fmt(date, format) {
      return date ? moment(date).format(format) : '—';
    },
  },
};
</script>
